/**
 * NewFillBlankQuiz Component
 * 새 UI - 주관식 문제 with textarea
 * - 정오답 표시 없음, 답안 제출만
 * - blank는 항상 1개, problem 렌더링 없음
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { cn } from '../../lib/utils'
import { KatexText, SubmitButton, QuizMedia } from './shared'
import type { FillBlankQuizData, QuizSolveResult } from '../../types/legacyQuiz'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'

interface NewFillBlankQuizProps {
  quizId: string | number
  quiz: FillBlankQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
}

export function FillBlankQuizForDiary({
  quizId,
  quiz,
  onSolve,
  solveResult,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
}: NewFillBlankQuizProps) {
  const { t } = useTranslation('quiz')
  const [startTime] = useState(() => Date.now())
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const isSolved = solveResult !== null && solveResult !== undefined

  // 초기 answer 계산 (항상 1개)
  const computedInitialAnswer = initialAnswersProp?.[0] ?? ''

  const [answer, setAnswer] = useState<string>(computedInitialAnswer)
  const isInitialMount = useRef(true)

  useEffect(() => {
    setAnswer(computedInitialAnswer)
  }, [computedInitialAnswer])

  // hideSubmitButton 모드에서 답변 변경 시 자동으로 onSolve 호출 (페이지 이동 시 상태 유지용)
  // Skip initial mount — parent already has the data from previousResponses.
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (hideSubmitButton && !isSolved && !isViewSolved) {
      onSolve?.(quizId, [answer], quiz.vars, Date.now() - startTime)
    }
  }, [answer, hideSubmitButton, isSolved, isViewSolved, onSolve, quizId, quiz.vars, startTime])

  const canSubmit = answer.trim().length > 0

  // 답안 입력 처리
  const handleAnswerChange = useCallback((value: string) => {
    if (isSolved || isViewSolved) return
    setAnswer(value)
  }, [isSolved, isViewSolved])

  // 제출 버튼 클릭
  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return
    const elapsedTime = Date.now() - startTime
    onSolve?.(quizId, [answer], quiz.vars, elapsedTime)
  }, [answer, canSubmit, isSolved, onSolve, quizId, quiz.vars, startTime])

  // Shift+Enter 제출 지원 (Enter는 줄바꿈)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.shiftKey && canSubmit && !isSolved) {
      e.preventDefault()
      handleSubmit()
    }
  }, [canSubmit, isSolved, handleSubmit])

  return (
    <div className="space-y-6">
      {/* 문제 제목 */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-3"
      >
        <div className="inline-flex gap-2 items-center px-3 py-1.5 text-sm font-bold text-white bg-linear-to-r from-emerald-500 to-teal-500 rounded-full shadow-2xs">
          <span>{t('types.newFillBlank.label')}</span>
        </div>
        <h3 className="text-xl font-bold leading-relaxed text-gray-900">
          <KatexText text={quiz.title} />
        </h3>
        <p className="text-base text-gray-400">
          {t('types.newFillBlank.note')}
        </p>
        {/* 문제 이미지/영상/Desmos */}
        <QuizMedia image={quiz.image} imageMobile={quiz.imageMobile} desmos={quiz.desmos} vars={quiz.vars} />
      </motion.div>

      {/* 답안 입력 영역 - Textarea */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <textarea
          ref={textareaRef}
          value={answer}
          onChange={(e) => handleAnswerChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSolved || isViewSolved}
          placeholder={t('types.newFillBlank.placeholder')}
          rows={4}
          className={cn(
            'w-full px-4 py-3 text-lg rounded-xl border-2 transition-all resize-none',
            'focus:outline-hidden focus:ring-2 focus:ring-offset-1',
            // 기본 상태
            !isSolved && 'border-gray-200 focus:border-emerald-400 focus:ring-emerald-100',
            // 제출 후
            isSolved && 'border-gray-300 bg-gray-50',
            // 비활성화
            (isSolved || isViewSolved) && 'cursor-not-allowed opacity-80'
          )}
        />
      </motion.div>

      {/* 제출 버튼 */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel ?? t('types.newFillBlank.submit')}
        />
      )}

      {/* 제출 완료 표시 */}
      {isSolved && (
        <div className="p-4 text-center text-gray-600 bg-gray-100 rounded-xl">
          {t('types.newFillBlank.submitted')}
        </div>
      )}
    </div>
  )
}
